// The trip window: "I'm in Ireland from the 12th to the 19th — what's on?".
// One control, used both in the filter panel and above the What's on list. It drives
// every dated feed (GAA matches, racing and eventing) through state/store.ts.
import { addDays, clearEventWindow, eventFrom, eventTo, eventWindowSet, localISODate, setEventWindow } from "../state/store";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Fri 12 Sep" */
export function fmtIso(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getDay()]} ${d} ${MON[m - 1]}`;
}

/** The Saturday and Sunday of the weekend we are in or heading into. */
function weekend(): [string, string] {
  const today = localISODate();
  const [y, m, d] = today.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay(); // 0 Sun … 6 Sat
  if (dow === 0) return [today, today];       // Sunday: the weekend is today
  return [addDays(today, 6 - dow), addDays(today, 7 - dow)];
}

const PRESETS: [string, () => [string, string]][] = [
  ["Today", () => [localISODate(), localISODate()]],
  ["This weekend", weekend],
  ["Next 7 days", () => [localISODate(), addDays(localISODate(), 6)]],
  ["Next 30 days", () => [localISODate(), addDays(localISODate(), 29)]],
];

export function windowLabel(): string {
  const f = eventFrom.value, t = eventTo.value;
  if (!f && !t) return "any date from today";
  if (f && t) return f === t ? `on ${fmtIso(f)}` : `${fmtIso(f)} to ${fmtIso(t)}`;
  if (f) return `from ${fmtIso(f)}`;
  return `up to ${fmtIso(t!)}`;
}

export function DateWindow() {
  const from = eventFrom.value ?? "";
  const to = eventTo.value ?? "";
  const today = localISODate();
  const set = (which: "from" | "to", v: string) => {
    const next = v || null;
    // Keep the pair in order rather than showing an empty list: dragging one end past
    // the other takes the other with it.
    if (which === "from") setEventWindow(next, next && to && next > to ? next : to || null);
    else setEventWindow(next && from && next < from ? next : from || null, next);
  };
  return (
    <div class="datewindow">
      <div class="row-2 datewindow-inputs">
        {/* A past date is not a trip: the list clamps to today anyway, so the input says so. */}
        <input type="date" value={from} min={today} aria-label="First day" onInput={(e) => set("from", (e.target as HTMLInputElement).value)} />
        <span class="sep">to</span>
        <input type="date" value={to} min={from || today} aria-label="Last day" onInput={(e) => set("to", (e.target as HTMLInputElement).value)} />
      </div>
      <div class="chips chips-dense">
        {PRESETS.map(([label, range]) => {
          const [a, b] = range();
          const on = eventFrom.value === a && eventTo.value === b;
          return (
            <button type="button" key={label} class={`chip ${on ? "is-on" : ""}`} aria-pressed={on}
              onClick={() => (on ? clearEventWindow() : setEventWindow(a, b))}>{label}</button>
          );
        })}
        {eventWindowSet.value && <button type="button" class="chip" onClick={clearEventWindow}>Any date</button>}
      </div>
    </div>
  );
}
